// Keyboard input → TankKey bitfield. Bindings match the on-screen hint.

import { TankKey } from '@bolo2k/sim';

const HOLD_KEYS: Record<string, number> = {
  KeyW: TankKey.Faster,
  ArrowUp: TankKey.Faster,
  KeyS: TankKey.Slower,
  ArrowDown: TankKey.Slower,
  KeyA: TankKey.TurnLeft,
  ArrowLeft: TankKey.TurnLeft,
  KeyD: TankKey.TurnRight,
  ArrowRight: TankKey.TurnRight,
  Space: TankKey.Shoot,
  Tab: TankKey.DropMine,
  Equal: TankKey.MoreRange,
  Minus: TankKey.LessRange,
};

export class Keyboard {
  private held = new Set<string>();

  attach(target: Window | HTMLElement = window): () => void {
    const onDown = (e: KeyboardEvent) => {
      // Skip the binding pass when a text input has focus (e.g. the chat
      // box). Otherwise typing "WASD" mid-message would also drive the tank.
      if (isTextEditing(document.activeElement)) return;
      if (e.code in HOLD_KEYS) {
        this.held.add(e.code);
        if (e.code === 'Tab' || e.code === 'Space') e.preventDefault();
      }
    };
    const onUp = (e: KeyboardEvent) => {
      // Always honour key-up so a key held when chat opens doesn't get stuck.
      this.held.delete(e.code);
    };
    const onBlur = () => this.held.clear();
    target.addEventListener('keydown', onDown as EventListener);
    target.addEventListener('keyup', onUp as EventListener);
    target.addEventListener('blur', onBlur);
    return () => {
      target.removeEventListener('keydown', onDown as EventListener);
      target.removeEventListener('keyup', onUp as EventListener);
      target.removeEventListener('blur', onBlur);
    };
  }

  /** Forcibly clear the held set. Useful when external UI (chat) takes
   *  focus mid-press; without this a key can stick until released again. */
  clear(): void {
    this.held.clear();
  }

  bitfield(): number {
    let bits = 0;
    for (const code of this.held) bits |= HOLD_KEYS[code] ?? 0;
    return bits;
  }
}

function isTextEditing(el: Element | null): boolean {
  if (!el) return false;
  if (el instanceof HTMLInputElement) return el.type === 'text' || el.type === 'search';
  if (el instanceof HTMLTextAreaElement) return true;
  return false;
}
